import { useRouter, useSearchParams } from 'next/navigation';
import {
  type FC,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import useSWRImmutable from 'swr/immutable';
import { setBonusCode as requestsSetBonusCode } from '@/api/auth';
import { CMSGeneralButtonType } from '@/api/cms/base';
import type { CMSBonus } from '@/api/cms/bonuses';
import {
  type BonusTermsAndConditionsApi,
  fetchBonusTermsAndConditions,
} from '@/api/cms/bonusTermsAndConditions';
import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { StaticModal } from '@/components/StaticModal';
import { TimerLabel } from '@/components/Timer';
import { TimerLabelSize } from '@/components/Timer/styled';
import { removeQueryParameter } from '@/helpers/common';
import { Modals } from '@/helpers/modals';
import { getTimeLeftFromScheduler } from '@/helpers/timer';
import { useFormatRakebackAmount } from '@/hooks/formatRakebackAmount';
import { useLoadImage } from '@/hooks/image';
import { useNavigation } from '@/hooks/navigation';
import { useGetCommonStore, useModalsStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { Spinner } from '@/styles/common';
import { BONUS_REF_PARAM, PROMO_HEADER_OFFSET } from '../../../helpers';
import {
  ScPromotionsCard,
  ScPromotionsCardLeft,
  ScPromotionsCardRight,
} from '../styled';
import { AnimatedParticles } from './AnimatedParticles';

interface CardProps extends CMSBonus {}

export const Card: FC<CardProps> = ({
  id,
  image,
  title,
  promoText,
  buttonType,
  buttonLink,
  termsAndConditions,
  identifier,
  scheduler,
  countdownToggle,
  code,
  starsAnimation,
}) => {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const open = useModalsStore.use.open();
  const { push } = useRouter();
  const searchParams = useSearchParams();
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const fetchUser = useCommonStore.use.fetchUser();

  const ref = useRef<HTMLDivElement | null>(null);
  const [isBonusSelected, setIsBonusSelected] = useState(false);
  const [pendingSetBonusCode, setPendingSetBonusCode] = useState(false);

  const timer = useMemo(
    () => (countdownToggle ? getTimeLeftFromScheduler(scheduler) : null),
    [scheduler, countdownToggle]
  );

  const handleDeposit = useCallback(async () => {
    if (code) {
      try {
        setPendingSetBonusCode(true);
        const { error } = await requestsSetBonusCode({
          depositBonusCode: code,
        });

        if (!error) {
          fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
        }
      } finally {
        setPendingSetBonusCode(false);
      }
    }

    open(Modals.Deposit);
  }, [code, open, fetchUser]);

  const button = useMemo(() => {
    switch (buttonType) {
      case CMSGeneralButtonType.DEPOSIT:
        if (isAuthenticated)
          return (
            <Button
              isLoading={pendingSetBonusCode}
              onClick={handleDeposit}
              secondary
              small
              wide
            >
              {t('common:button.deposit')}
            </Button>
          );

        return (
          <Button onClick={() => open(Modals.Registration)} primary small wide>
            {t('common:button.signUp')}
          </Button>
        );

      case CMSGeneralButtonType.SHOW_MORE:
        if (!buttonLink) return null;

        return (
          <Button primary small to={buttonLink} wide>
            {t('common:showMore')}
          </Button>
        );

      case CMSGeneralButtonType.EXPLORE:
        if (!buttonLink) return null;

        return (
          <Button primary small to={buttonLink} wide>
            {t('common:button.explore')}
          </Button>
        );

      case CMSGeneralButtonType.READ_MORE:
        if (!buttonLink) return null;

        return (
          <Button primary small to={buttonLink} wide>
            {t('common:button.readMore')}
          </Button>
        );

      case CMSGeneralButtonType.JOIN_NOW:
        if (!buttonLink) return null;

        return (
          <Button primary small to={buttonLink} wide>
            {t('common:button.joinNow')}
          </Button>
        );

      case CMSGeneralButtonType.PLAY_NOW:
        if (!buttonLink) return null;

        return (
          <Button primary small to={buttonLink} wide>
            {t('common:button.playNow')}
          </Button>
        );

      default:
        return null;
    }
  }, [
    buttonType,
    isAuthenticated,
    handleDeposit,
    pendingSetBonusCode,
    t,
    buttonLink,
    open,
  ]);

  useEffect(() => {
    if (!searchParams.get(BONUS_REF_PARAM)) return;

    setIsBonusSelected(searchParams.get(BONUS_REF_PARAM) === identifier);

    removeQueryParameter(BONUS_REF_PARAM, push);
  }, [identifier, navigate, push, searchParams]);

  useEffect(() => {
    if (isBonusSelected) {
      window.scrollTo({
        behavior: 'smooth',
        top:
          (ref?.current?.getBoundingClientRect().top || 0) -
          PROMO_HEADER_OFFSET,
      });
    }
  }, [isBonusSelected]);

  const formattedTitle = useFormatRakebackAmount(title);

  const formattedPromoText = useFormatRakebackAmount(promoText);

  return (
    <ScPromotionsCard ref={ref}>
      {image && (
        <PromotionsCardLeft bg={image?.url || ''}>
          {starsAnimation && (
            <AnimatedParticles
              images={[
                {
                  src: '/bonuses/particles/big-star.svg',
                  position: { x: 20, y: -5 },
                },
                {
                  src: '/bonuses/particles/small-star.svg',
                  position: { x: -5, y: 70 },
                },
                {
                  src: '/bonuses/particles/smallest-star.svg',
                  position: { x: 93, y: 40 },
                },
                {
                  src: '/bonuses/particles/smallest-star.svg',
                  position: { x: 75, y: -4 },
                },
              ]}
            />
          )}
          <ImageComponent
            alt="promo image"
            className="promo-image"
            fill
            priority
            src={image?.url || ''}
          />
        </PromotionsCardLeft>
      )}
      <ScPromotionsCardRight>
        <div>
          <header>
            <h2>{formattedTitle}</h2>
            {!!timer && (
              <TimerLabel gray size={TimerLabelSize.AUTO} timeLeft={timer} />
            )}
          </header>
          {promoText && (
            <div
              className="description"
              dangerouslySetInnerHTML={{ __html: formattedPromoText }}
            />
          )}
        </div>
        <div className="buttons">
          {button}
          {termsAndConditions && (
            <TermsAndConditionsModal
              id={id}
              identifier={identifier}
              termsAndConditions={termsAndConditions}
              title={title}
            />
          )}
        </div>
      </ScPromotionsCardRight>
    </ScPromotionsCard>
  );
};

export const PromotionsCardLeft: FC<PropsWithChildren<{ bg: string }>> = ({
  bg,
  children,
}) => {
  const { loading } = useLoadImage(bg);

  const { blackColor, mainColor } = useTheme();

  return (
    <ScPromotionsCardLeft>
      {loading ? (
        <Spinner background={blackColor} center color={mainColor} size={80} />
      ) : (
        children
      )}
    </ScPromotionsCardLeft>
  );
};

interface TermsAndConditionsModalProps
  extends Pick<
    CMSBonus,
    'id' | 'title' | 'termsAndConditions' | 'identifier'
  > {}

const TermsAndConditionsModal: FC<TermsAndConditionsModalProps> = (props) => {
  const { t } = useTranslation();

  const [isOpenTerms, setIsOpenTerms] = useState(false);

  const onCloseTerms = (e?: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e?.preventDefault();
    setIsOpenTerms((s) => !s);
  };

  return (
    <>
      <Button
        autoPaddingHorizontal
        height="auto"
        onClick={onCloseTerms}
        transparent
      >
        {t('common:button.termsAndConditions')}
      </Button>
      {isOpenTerms && <BonusModal {...props} onClose={onCloseTerms} />}
    </>
  );
};

interface BonusModalProps
  extends Pick<CMSBonus, 'title' | 'termsAndConditions'> {
  onClose: () => void;
  name?: string;
}

const BonusModal: FC<BonusModalProps> = ({
  title,
  termsAndConditions,
  onClose,
}) => {
  const {
    i18n: { language },
  } = useTranslation();

  const { data, isLoading } = useSWRImmutable<BonusTermsAndConditionsApi>(
    ['bonusTermsAndConditions', language],
    () => fetchBonusTermsAndConditions(language)
  );

  return (
    <StaticModal onClose={onClose} pending={isLoading} title={title}>
      {data ? (
        <>
          <div dangerouslySetInnerHTML={{ __html: termsAndConditions }} />
          <div dangerouslySetInnerHTML={{ __html: data.text }} />
        </>
      ) : null}
    </StaticModal>
  );
};
